import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ThreeOptionSlider } from '../components/ThreeOptionsSelector.tsx';
import { DataTable } from '../components/DataTable.tsx';
import { useReactTable, getCoreRowModel, getSortedRowModel, createColumnHelper } from '@tanstack/react-table';
import { SearchInput } from '../components/Input.tsx';
import { PeriodSelector } from '../components/PeriodSelector.tsx';
import { MultiSelectInfo, SelectionInfo } from '../components/SelectionInfo.tsx';
import { ActionButton } from '../components/SelectionInfo.tsx';
import { ActionButton as ActionButton2 } from '../components/UserModal.tsx';
import axiosInstance from '../axiosInstance.tsx';
import { useIsMobile } from '../App.tsx';

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 20px;
  gap: 36px;
     @media (max-width: 768px) {
    padding: 0;
    gap: 24px;
    padding-bottom: 15%
  }
`;

type Payment = {
  id: number;
  user_id: number;
  amount: number;
  creationDate: string;
};

// Пример данных
const paymentsData: Payment[] = [
  { id: 1, user_id: 101, amount: 350, creationDate: '2024-01-15' },
  { id: 2, user_id: 102, amount: 420, creationDate: '2024-01-17' },
  { id: 3, user_id: 103, amount: 120, creationDate: '2024-01-16' },
  { id: 4, user_id: 104, amount: 550, creationDate: '2024-01-15' },
];

const paymentColumnHelper = createColumnHelper<Payment>();

const paymentColumns = [
  paymentColumnHelper.accessor('username', {
    header: 'Username',
    cell: info => info.getValue(),
  }),
  paymentColumnHelper.accessor('amount', {
    header: 'Сумма',
    cell: info => `$${info.getValue().toFixed(2)}`,
  }),
    paymentColumnHelper.accessor('creation_date', {
    header: 'Дата создания',
    cell: info => `${info.getValue()}`,
  }),
];


const userColumnHelper = createColumnHelper<Payment>();

const userColumns = [
  userColumnHelper.accessor('username', {
    header: 'Username',
    cell: info => info.getValue(),
  }),
  userColumnHelper.accessor('amount', {
    header: 'Баланс',
    cell: info => `$${info.getValue().toFixed(2)}`,
  })
];


const TableContainer = styled.div`
  display: flex;
  background-color: none;
  flex-direction: column;

  gap: 10px; // расстояние между инпутом и таблицей
`;

export const PaymentsPage: React.FC = () => {
  const [selectedOption, setSelectedOption] = useState<string>('Ждут выплату');
  const [sorting, setSorting] = useState<any[]>([]);
  const [searchId, setSearchId] = useState<string>('');
  const [data, setData] = useState<Payment[]>([]);
    const [loading, setLoading] = useState<boolean>(false);





  const [paymentsSummary, setPaymentsSummary] = useState({pending: null, topBalances: null});





  useEffect(() => { 

    const method = {"Ждут выплату": "paymentsUnpaid", "Выплачено": "paymentsPaid", "Топ балансов": "topBalances"}[selectedOption];
    setLoading(true);
    axiosInstance.get(`/${method}`).then(response => {
        setData(response.data);
        setLoading(false);
    }); 
    }, [selectedOption]);


  useEffect(() => { 

    axiosInstance.get('/paymentsSummary').then(response => {
        setPaymentsSummary(response.data);
    });


  }, [selectedOption]);


// Парсит строку "11,838.2$" -> 11838.2
const parseMoney = (str: string | number): number => {
  if (typeof str === "number") return str;
  const cleaned = str.toString().replace(/[^0-9.]/g, "");
  return parseFloat(cleaned) || 0;
};

// Форматирует число 11838.2 -> "11,838.20$"
const formatMoney = (num: number): string => {
  return num.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + "$";
};

const payUnpaidWithdrawals = (ids: number[]) => {
  let total = 0;
  ids.forEach((id) => {
    const payment = data.find(x => x.id === id);
    if (payment) total += parseMoney(payment.amount);
  });

  axiosInstance.post('/payUnpaidWithdrawals', { ids }).then(() => {
    setData(prevData => prevData.filter(payment => !ids.includes(payment.id)));
    setPaymentsSummary(prevSummary => ({
      ...prevSummary,
      pending: formatMoney(parseMoney(prevSummary.pending) - total)
    }));
  });
};

const withdrawBalance = (id: number) => {
  const payment = data.find(x => x.id === id);
  const amount = payment ? parseMoney(payment.amount) : 0;

  axiosInstance.post('/withdrawBalance', { id }).then(() => {
    setData(prevData => prevData.filter(payment => payment.id !== id));
    setPaymentsSummary(prevSummary => ({
      ...prevSummary,
      topBalances: formatMoney(parseMoney(prevSummary.topBalances) - amount)
    }));
  });
};



  const [selectionIds, setSelectionIds] = useState<Set<number>>(new Set());

  const onConcretePaymentClick = (paymentId: number) => {
    if(selectedOption == "Ждут выплату") {
        payUnpaidWithdrawals([paymentId]);
    }
    if(selectedOption == "Топ балансов") {
        withdrawBalance(paymentId);
    }
  }

  const onActionClick = () => {
    if(selectedOption == "Ждут выплату") {
        payUnpaidWithdrawals(Array.from(selectionIds));
    }
  }

    const isMobile = useIsMobile();


    const usedColumns = React.useMemo(
      () => selectedOption != "Топ балансов" ? (isMobile ? paymentColumns.filter((x) => x.accessorKey  != "creation_date") : paymentColumns) : userColumns,
      [selectedOption, isMobile]
    )

    const filteredData = React.useMemo(
    () => (searchId ? data.filter(user => ((user.username ? user.username:  user.id.toString()).toLowerCase()).includes(searchId.toLowerCase())) : data),
    [searchId, data]
    );


      // Создаём table объект
  const table = useReactTable({
    data: filteredData,
    columns: usedColumns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });



  return (
    <PageWrapper>
          <ThreeOptionSlider
        options={[
          { label: 'Ждут выплату', hint: paymentsSummary.pending },
          { label: 'Выплачено' },
          { label: 'Топ балансов', hint: paymentsSummary.topBalances  },
        ]}
        value={selectedOption}
        onChange={!loading ? setSelectedOption : (value) => {} }
      />
       {true && <TableContainer>
    
            {selectedOption != "Выплачено" && <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
                
        <SearchInput
            value={searchId}
            onChange={setSearchId}
            placeholder="Поиск"
        />
        {selectedOption == "Ждут выплату" && (isMobile ? <ActionButton2 payment={true} variant='block' active={selectionIds.size > 0}>Выплатить ({selectionIds.size})</ActionButton2> :
        <MultiSelectInfo allSelected={selectionIds.size == data.length} count={selectionIds.size} onAction={onActionClick} actionLabel={'Выплатить'} onSelectAll={() => {
                    setSelectionIds(new Set(data.map(p => p.id)));
                  } } onClose={() => {
                      setSelectionIds(new Set());
                        

                  } }/>)}
        </div>}
      <DataTable selectable={selectedOption == "Ждут выплату"}
        table={table}
        loading={loading}
        expanded={false}
        expandedRows={new Set()}
        setExpandedRows={() => {}}
        toggleRow={() => {}}
        selectedRows={selectionIds}
        setSelectedRows={setSelectionIds}
        renderExpandedRow={null}
        selectionIds={selectionIds}
        after={selectedOption != "Выплачено" ? (row) => (
  <div style={{ display: "flex", justifyContent: "flex-end", marginRight: "30px" }}>
    <ActionButton onClick={() => onConcretePaymentClick(row.id)}>
      Выплатить
    </ActionButton>
  </div>
) : null}
        onSelectionChange={(ids) => setSelectionIds(new Set(ids))}
      />
      </TableContainer>}
    </PageWrapper>
  );
};
